/**
 * D420: the motorsport theme's map kit (`MapKit` in `iso-kit.ts`): a team growing from a lock-up
 * garage to a works team. Its plots are the farm's places under the motorsport producers' ids
 * (the shared economy keeps every position, D351), every one drawn as a plot of its own kit that
 * grows tall as well as wide (`iso-kit-motorsport-art.ts`), painted with the compute theme's
 * pieces (`paintUnit`). The paddock stands on tarmac and concrete inside a tyre wall; the farm's
 * track is the service road; the country round it is open grass with copses and, in place of a
 * river, a short race circuit beyond the back of the land, its pits and grandstand beside it and
 * a fans' car park by the road in. Seasons and the hour stay: snow on the roofs in winter, lit
 * garages and floodlights at night. Its 3D half is `motorsportThree` in
 * `idle/three/motorsport.ts`.
 */
import { getIdleContent } from '@hedgerow/data';
import type { CountryItem, WorldLayout } from '../idle/map.ts';
import {
  ASSET_PALETTE,
  FIELD_CYCLE,
  GOOD_WEATHER,
  LANDSCAPE,
  PHASE_LIGHT,
  PHASE_TIME,
  READY_BADGE,
  SNOW,
} from '../idle/appearance.ts';
import { THEME_MAP_COLOURS } from '../idle/assets/idle-worlds-assets.js';
import type { IsoBuildingAt, Standing } from './IsoWorldMap.tsx';
import {
  alpha,
  depth,
  isPlan,
  mix,
  palette,
  parcelBoundaries,
  polyPath,
  project,
  rectPts,
  rnd,
  scatter,
  shade,
  unionBounds,
  unionPath,
  heartPlan,
  TW,
  TH,
  assetLevel,
  levelHeight,
  type Bounds,
  type Heart,
  type IsoParcel,
  type MapTokens,
  type Palette,
  fameProgress,
  heartForm,
} from './iso.ts';
import { paintProp, type Prop, type Puff } from './iso-paint.ts';
import type { FlatEdges, FlatScenery, MapKit, PlotSlot } from './iso-kit.ts';
import { SLOTS as FARM_SLOTS, CHAIN_BUILDINGS as FARM_CHAINS } from './iso-kit-farm.ts';
import {
  C,
  PARK,
  WINTER,
  hashXY,
  unitTop,
  type Look,
  type Piece,
  type Unit,
} from './iso-kit-compute-art.ts';
import {
  BLINK_CSS,
  ZPX,
  corner,
  lookOf,
  ordered,
  paintPiece,
  paintUnit,
  paintWeather,
  piece,
} from './iso-kit-compute.ts';
import {
  GANTRIES,
  KERBS,
  LIVERIES,
  M,
  TYRE_WALLS,
  buildingUnits,
  car,
  markUnits,
} from './iso-kit-motorsport-art.ts';

/* ---------- the plots and chain buildings: the farm's places, by position (D351) ---------- */

/** The farm's slot for each position, under the motorsport producer at that position. */
function motorsportPlots(): Record<string, PlotSlot> {
  const farm = getIdleContent('farm'),
    theme = getIdleContent('motorsport');
  const landOf = new Map(farm.map.lands.map((l, i) => [l.id, theme.map.lands[i]?.id ?? l.id]));
  const out: Record<string, PlotSlot> = {};
  farm.producers.forEach((p, i) => {
    const id = theme.producers[i]?.id;
    const s = FARM_SLOTS[p.id];
    if (!id || !s) return;
    out[id] = {
      x: s.x,
      y: s.y,
      w: s.w,
      h: s.h,
      kind: 'field',
      ...(s.chainReserve !== undefined ? { chainReserve: s.chainReserve } : {}),
      ...(s.land ? { land: landOf.get(s.land) ?? s.land } : {}),
    };
  });
  return out;
}

/** Each chain's building where the farm's chain at that position stands. */
function motorsportChains(): Record<string, IsoBuildingAt> {
  const farm = getIdleContent('farm'),
    theme = getIdleContent('motorsport');
  const out: Record<string, IsoBuildingAt> = {};
  farm.chains.forEach((c, i) => {
    const id = theme.chains[i]?.id;
    const b = FARM_CHAINS[c.id];
    if (id && b) out[id] = { ...b, kind: 'shed', use: 'processing' };
  });
  return out;
}

/* ---------- the palette ---------- */

/** The green the paddock's verges and the country lean to: well kept, as a circuit's is. */
const TURF = '#62B060';

function motorsportPalette(
  season: string,
  time: string,
  tokens?: MapTokens | null,
  weather?: Parameters<typeof palette>[3],
): Palette {
  const P = palette(season, time, tokens, weather);
  const [g, gt] = P.S.ground;
  const frost = !!P.S.frost;
  return {
    ...P,
    soil: mix(M.concrete, g, gt * 0.4),
    yard: mix('#7D8086', g, gt * 0.2),
    road: mix(M.tarmac, g, 0.06),
    track: mix(M.tarmac, g, 0.06),
    waste: mix(M.gravel, g, gt * 0.3),
    hedge: M.tyre,
    meadow: frost
      ? mix(mix(P.meadow, TURF, 0.55), SNOW.colour, WINTER.grass)
      : mix(P.meadow, TURF, 0.6),
    pasture: frost
      ? mix(mix(P.pasture, TURF, 0.5), SNOW.colour, WINTER.grass * 0.8)
      : mix(P.pasture, TURF, 0.55),
    water: mix(C.pond, P.water, 0.4),
  };
}

/* ---------- D420: the country round the paddock ---------- */

const overlaps = (a: Bounds, b: Bounds): boolean =>
  a.x < b.x + b.w && b.x < a.x + a.w && a.y < b.y + b.h && b.y < a.y + a.h;

const pad = (b: Bounds, n: number): Bounds => ({
  x: b.x - n,
  y: b.y - n,
  w: b.w + 2 * n,
  h: b.h + 2 * n,
});

let landscapeCache: { key: string; out: CountryItem[] } | null = null;

/**
 * The country round the paddock, from its seed. D437: in place of the farm's river, the circuit
 * in the margin round the land, wrapping it on the two sides away from the gate (`mcircuit`, the
 * ground its grand prix layout takes on each side, `r` 1 for the side across x; `k` its site,
 * `circuitSite`). The road out of the gate (`mroad`), a fans' car park beside it
 * (`mpark`), copses and lone trees (`mtree`, `s` the size), houses where the world's kind has a
 * town (`mhouse`, `s` the storeys), and the shared `chills`.
 */
export function motorsportLandscape(L: WorldLayout, plot: Bounds, lands: Bounds[]): CountryItem[] {
  const key = JSON.stringify([L.seed, plot, lands, L.gate, L.scenery, L.width, L.height]);
  if (landscapeCache?.key === key) return landscapeCache.out;
  const sc = L.scenery;
  const S = L.seed >>> 0;
  const R = LANDSCAPE.reach;
  const out: CountryItem[] = [];
  out.push({ t: 'chills', x: plot.x - R, y: plot.y - R, w: 0, h: 0, s: 0.45 * sc.hills });
  // Trees and the car park keep clear of the land in use and of the circuit.
  const keep: Bounds[] = [pad(plot, 2), ...lands.map((b) => pad(b, 2))];
  // The road out of the gate, on to the edge of the country.
  const g = L.gate;
  const dir: [number, number] = g.y >= L.height ? [0, 1] : g.x <= 0 ? [-1, 0] : [1, 0];
  const far = R + Math.max(plot.w, plot.h);
  const road: Bounds =
    dir[1] === 0
      ? { x: dir[0] < 0 ? g.x - far : g.x, y: g.y - 0.45, w: far, h: 0.9 }
      : { x: g.x - 0.45, y: g.y, w: 0.9, h: far };
  out.push({ t: 'mroad', ...road });
  keep.push(pad(road, 1));
  // D437: the circuit, in the margin on the two sides of the land away from the gate.
  const site = circuitSite(L, plot, road);
  for (const c of site ? circuitItems(site) : []) {
    out.push(c);
    keep.push(pad(c, 1.2));
  }
  // The fans' car park beside the road in.
  const parkB: Bounds =
    dir[1] === 0
      ? { x: dir[0] < 0 ? g.x - 9 : g.x + 3, y: g.y - 5.2, w: 6, h: 4 }
      : { x: g.x + 1.4, y: g.y + 3, w: 4, h: 6 };
  if (!keep.some((k) => overlaps(k, parkB))) {
    out.push({ t: 'mpark', ...parkB });
    keep.push(pad(parkB, 0.5));
  }
  // A town along the road, where the world's kind has one.
  if (sc.houses > 0)
    for (let i = 0; i < 6 + sc.houses * 2; i++) {
      const a = (0.1 + 0.8 * hashXY(S, i, 21)) * (Math.PI / 2);
      const d = 14 * sc.village + hashXY(S, i, 22) * 6;
      const b: Bounds = {
        x: plot.x + plot.w + Math.cos(a) * d * 0.6,
        y: plot.y + plot.h + Math.sin(a) * d * 0.6,
        w: 1 + hashXY(S, i, 23) * 0.8,
        h: 1 + hashXY(S, i, 24) * 0.8,
      };
      if (keep.some((k) => overlaps(k, b))) continue;
      out.push({ t: 'mhouse', ...b, s: 1 + Math.floor(hashXY(S, i, 25) * 3) });
      keep.push(pad(b, 0.3));
    }
  // Copses and lone trees across the open grass.
  const reach: Bounds = { x: plot.x - R, y: plot.y - R, w: plot.w + 2 * R, h: plot.h + 2 * R };
  const rf = rnd(S ^ 0x51);
  const copses = Math.round(34 * sc.copse);
  for (let i = 0; i < copses; i++) {
    const cx = reach.x + rf() * reach.w,
      cy = reach.y + rf() * reach.h;
    const n = 4 + Math.floor(rf() * 6);
    for (let q = 0; q < n; q++) {
      const x = cx + (rf() - 0.5) * 3,
        y = cy + (rf() - 0.5) * 3;
      if (keep.some((k) => overlaps(k, { x: x - 0.3, y: y - 0.3, w: 0.6, h: 0.6 }))) continue;
      out.push({ t: 'mtree', x, y, w: 0, h: 0, s: 0.8 + rf() * 0.6 });
    }
  }
  for (let i = 0; i < 140 * sc.copse; i++) {
    const x = reach.x + rf() * reach.w,
      y = reach.y + rf() * reach.h;
    if (keep.some((k) => overlaps(k, { x: x - 0.3, y: y - 0.3, w: 0.6, h: 0.6 }))) continue;
    out.push({ t: 'mtree', x, y, w: 0, h: 0, s: 0.7 + rf() * 0.5 });
  }
  landscapeCache = { key, out };
  return out;
}

/* ---------- D420: the country's standing things, for both renderers ---------- */

/** A house of the town: brick, pitched in grey, its storeys from `s`. */
export function houseUnit(c: CountryItem): Unit {
  const floors = c.s ?? 1;
  const wall = ['#B4694A', '#D8CFBF', '#A5583F', '#E2DAC8'][
    Math.floor(hashXY(c.x, c.y, 27) * 4) % 4
  ] as string;
  return {
    x: c.x,
    y: c.y,
    w: c.w,
    d: c.h,
    pieces: [
      {
        s: 'box',
        x: c.x,
        y: c.y,
        w: c.w,
        d: c.h,
        z: 0,
        h: floors * 0.3 + 0.1,
        c: wall,
        top: '#6B6F75',
        roof: true,
        marks: { kind: 'win', rows: floors, cols: 3, c: '#5D7387', on: C.lit, lit: 0.5 },
      },
    ],
  };
}

/** The cars in the fans' car park, in rows on the grass. */
export function parkCars(
  c: CountryItem,
): Array<{ x: number; y: number; c: string; along: boolean }> {
  const out: Array<{ x: number; y: number; c: string; along: boolean }> = [];
  const alongX = c.w < c.h;
  for (let a = 0.5; a < (alongX ? c.w : c.h) - 0.3; a += 1.2)
    for (let b = 0.4; b < (alongX ? c.h : c.w) - 0.2; b += 0.4) {
      if (hashXY(a + c.x, b + c.y, 31) < 0.3) continue;
      const colour = PARK.cars[
        Math.floor(hashXY(a, b, 33) * PARK.cars.length) % PARK.cars.length
      ] as string;
      out.push(
        alongX
          ? { x: c.x + a, y: c.y + b, c: colour, along: true }
          : { x: c.x + b, y: c.y + a, c: colour, along: false },
      );
    }
  return out;
}

/** D381: a piece's colours passed through `tone` (the haze), its lit colour left alone. */
export function tonePiece(q: Piece, tone: (c: string) => string): Piece {
  const out = { ...q, c: tone(q.c) } as Piece;
  if (out.s === 'box') {
    if (out.top) out.top = tone(out.top);
    if (out.marks) out.marks = { ...out.marks, c: tone(out.marks.c) };
  }
  return out;
}

/** The country's standing units, by item. */
export function countryUnit(c: CountryItem): Unit | null {
  switch (c.t) {
    case 'mhouse':
      return houseUnit(c);
    default:
      return null;
  }
}

/* ---------- painting the country ---------- */

const f1 = (n: number): string => n.toFixed(1);
const far = (P: Palette, c: string): string => P.far(c);

/** Whether a footprint lies wholly behind the land in use at this turn. */
function behind(b: Bounds, plot: Bounds): boolean {
  const corners = (q: Bounds) =>
    [
      [q.x, q.y],
      [q.x + q.w, q.y],
      [q.x, q.y + q.h],
      [q.x + q.w, q.y + q.h],
    ].map(([a, c]) => depth(a as number, c as number));
  return Math.max(...corners(b)) < Math.min(...corners(plot)) - 1;
}

/** A unit standing in the country, hazed as the country is. */
function hazedUnit(u: Unit, P: Palette): Unit {
  return { ...u, fine: [], pieces: u.pieces.map((q) => tonePiece(q, (k) => far(P, k))) };
}

function paintCountry(s: FlatScenery, P: Palette, o: string[]): void {
  const { far: plot, country } = s;
  o.push(BLINK_CSS);
  const R = LANDSCAPE.flatReach;
  o.push(
    `<path d="${polyPath(rectPts(plot.x - R - 4, plot.y - R - 4, plot.w + 2 * R + 8, plot.h + 2 * R + 8))}" fill="${far(P, P.pasture)}"/>`,
  );
  const edge = { x0: plot.x - R, y0: plot.y - R, x1: plot.x + plot.w + R, y1: plot.y + plot.h + R };
  const clip = (c: Bounds): Bounds | null => {
    const x0 = Math.max(c.x, edge.x0),
      y0 = Math.max(c.y, edge.y0),
      x1 = Math.min(c.x + c.w, edge.x1),
      y1 = Math.min(c.y + c.h, edge.y1);
    return x1 > x0 && y1 > y0 ? { x: x0, y: y0, w: x1 - x0, h: y1 - y0 } : null;
  };
  const L = lookOf(P);
  const tarmac = far(P, P.road);
  // D437: the circuit at the heart's form, from the same site and form as the 3D map's.
  const age = s.age ?? { years: 0, generations: 0 };
  const circuit = circuitPlan(country, circuitForm(s.parcels ?? [], age.years));
  if (circuit) paintCircuit(circuit, P, o);
  for (const c of country) {
    if (c.t === 'mroad') {
      const b = clip(c);
      if (b) o.push(`<path d="${polyPath(rectPts(b.x, b.y, b.w, b.h))}" fill="${tarmac}"/>`);
    } else if (c.t === 'mpark') {
      const b = clip(c);
      if (!b) continue;
      o.push(
        `<path d="${polyPath(rectPts(b.x, b.y, b.w, b.h))}" fill="${far(P, shade(P.pasture, 0.08))}"/>`,
      );
      for (const q of parkCars(c)) {
        const [w, d] = q.along ? [0.22, 0.42] : [0.42, 0.22];
        o.push(
          `<path d="${polyPath(rectPts(q.x - w / 2, q.y - d / 2, w, d))}" fill="${far(P, q.c)}"/>`,
        );
      }
    }
  }
  // What stands: trees, the circuit's gantry, pit wall, grandstand and screen, the town; behind the land at
  // this turn (one in front would be painted under the slab, so it lies flat).
  const stand: Array<{ d: number; u: Unit }> = [];
  // D426: the module's country, as the 3D map builds it, in place of the layout's copses.
  const plan = sceneryPlan(
    plot,
    country,
    s.lands ?? [],
    // D430: grown with the world's years and generations, as the module grows it.
    fameProgress(sceneryProgress(s.parcels ?? []), age),
  );
  // The country's own things stand wherever they are, as the layout's copses did; only the
  // circuit's buildings lie flat in front of the land.
  for (const it of plan.items) {
    const u = sceneryUnit(it);
    if (clip({ x: u.x, y: u.y, w: Math.max(0.05, u.w), h: Math.max(0.05, u.d) }))
      stand.push({ d: depth(it.x, it.z), u: hazedUnit(u, P) });
  }
  // D437: the circuit hugs the fence, so its things are not hazed as the far country is.
  for (const u of circuit ? circuitUnits(circuit) : []) {
    const b = { x: u.x, y: u.y, w: Math.max(0.05, u.w), h: Math.max(0.05, u.d) };
    if (!clip(b)) continue;
    if (!behind(b, plot)) {
      // In front of the land it would be painted under the slab, so it lies flat, in its colour.
      const c = (u.pieces[0] as { c?: string } | undefined)?.c ?? M.white;
      o.push(`<path d="${polyPath(rectPts(b.x, b.y, b.w, b.h))}" fill="${shade(c, 0.1)}"/>`);
      continue;
    }
    stand.push({ d: depth(b.x + b.w / 2, b.y + b.h / 2), u });
  }
  for (const c of country) {
    if (c.t === 'mtree') continue;
    const u = countryUnit(c);
    if (!u) continue;
    const b = { x: u.x, y: u.y, w: Math.max(0.05, u.w), h: Math.max(0.05, u.d) };
    if (!clip(b)) continue;
    if (!behind(b, plot)) {
      o.push(
        `<path d="${polyPath(rectPts(b.x, b.y, b.w, b.h))}" fill="${shade(far(P, M.white), 0.1)}"/>`,
      );
      continue;
    }
    stand.push({ d: depth(b.x + b.w / 2, b.y + b.h / 2), u: hazedUnit(u, P) });
  }
  // The car out on the circuit: the 3D map's laps, held still here.
  const pts = circuit?.line;
  if (pts) {
    const i = Math.round(pts.length * 0.3);
    const a = pts[i] as [number, number],
      b = pts[(i + 1) % pts.length] as [number, number];
    const alongX = Math.abs(b[0] - a[0]) > Math.abs(b[1] - a[1]);
    if (behind({ x: a[0] - 0.3, y: a[1] - 0.3, w: 0.6, h: 0.6 }, plot))
      stand.push({
        d: depth(a[0], a[1]),
        u: {
          x: a[0] - 0.3,
          y: a[1] - 0.3,
          w: 0.6,
          d: 0.6,
          pieces: car(a[0], a[1], alongX, 'open', LIVERIES[2]),
        },
      });
  }
  stand.sort((a, b) => a.d - b.d);
  for (const s2 of stand) for (const q of ordered(s2.u)) paintPiece(q, P, L, o);
}

/* ---------- the ground, the tyre wall and the gantry ---------- */

/** A parcel's ground: a plot's pad, the paddock's tarmac, grass. */
export function groundColour(p: IsoParcel, P: Palette): string {
  const winter = !!P.S.frost;
  const snow = (c: string, k = 1) => (winter ? mix(c, SNOW.colour, WINTER.hard * k) : c);
  switch (p.t) {
    case 'field':
      return snow(mix(M.pad, P.soil, 0.25), 0.8);
    case 'yard':
      return snow(P.yard, 0.7);
    case 'pond':
      return shade(P.water, 0.1);
    case 'waste':
      return snow(P.waste, 0.6);
    case 'meadow':
      return p.mark || p.ground ? snow(mix('#D9D3C6', P.soil, 0.3), 0.8) : P.meadow;
    default:
      return snow(P.pasture);
  }
}

function paintGround(p: IsoParcel, P: Palette, o: string[]): void {
  if (!p.tiles.length) return;
  const fill = groundColour(p, P);
  o.push(
    `<path data-testid="iso-parcel" d="${unionPath(p.tiles)}" fill="${fill}" stroke="${shade(fill, 0.12)}" stroke-width="1"/>`,
  );
  if (p.t === 'yard') {
    // The paddock's painted bays along its front row, in the kerb decoration's colour.
    const b = unionBounds(p.tiles);
    const line = alpha(KERBS[p.path ?? ''] ?? M.line, 0.85);
    const y = b.y + b.h - 0.9;
    for (let x = b.x + 0.5; x < b.x + b.w; x += 0.5) {
      const a = project(x, y),
        e = project(x, y + 0.75);
      o.push(
        `<line x1="${f1(a[0])}" y1="${f1(a[1])}" x2="${f1(e[0])}" y2="${f1(e[1])}" stroke="${line}" stroke-width="1"/>`,
      );
    }
  }
}

/** The tyre wall: stacked tyres along a line, a band of the decoration's colour round them. */
function paintTyreWall(
  x1: number,
  y1: number,
  x2: number,
  y2: number,
  look: string | undefined,
  o: string[],
): void {
  const T = TYRE_WALLS[look ?? ''] ?? (TYRE_WALLS.tyres_black as (typeof TYRE_WALLS)[string]);
  const hpx = 0.07 * T.rows * ZPX;
  const a = project(x1, y1),
    b = project(x2, y2);
  if (isPlan()) {
    o.push(
      `<line x1="${f1(a[0])}" y1="${f1(a[1])}" x2="${f1(b[0])}" y2="${f1(b[1])}" stroke="${T.tyre}" stroke-width="2.4"/>`,
    );
    return;
  }
  o.push(
    `<path d="M${f1(a[0])} ${f1(a[1])} L${f1(b[0])} ${f1(b[1])} L${f1(b[0])} ${f1(b[1] - hpx)} L${f1(a[0])} ${f1(a[1] - hpx)} Z" fill="${T.tyre}"/>`,
  );
  o.push(
    `<line x1="${f1(a[0])}" y1="${f1(a[1] - hpx * 0.55)}" x2="${f1(b[0])}" y2="${f1(b[1] - hpx * 0.55)}" stroke="${T.band}" stroke-width="1.1"/>`,
  );
  o.push(
    `<line x1="${f1(a[0])}" y1="${f1(a[1] - hpx)}" x2="${f1(b[0])}" y2="${f1(b[1] - hpx)}" stroke="${M.tyreSide}" stroke-width="1"/>`,
  );
}

function paintEdges(e: FlatEdges, P: Palette, o: string[]): void {
  const { parcels, outline, lands, hedges, decor } = e;
  for (const g of parcelBoundaries(parcels.map((p) => p.tiles))) {
    const a = project(g.x1, g.y1),
      b = project(g.x2, g.y2);
    o.push(
      `<line x1="${f1(a[0])}" y1="${f1(a[1])}" x2="${f1(b[0])}" y2="${f1(b[1])}" stroke="${alpha('#ECE9E2', 0.85)}" stroke-width="1.6"/>`,
    );
  }
  if (!hedges) return;
  for (const [x0, y0, x1, y1] of outline?.hedges ?? [])
    paintTyreWall(x0, y0, x1, y1, decor?.hedge, o);
  for (const l of lands) {
    paintTyreWall(l.x, l.y, l.x + l.w, l.y, decor?.hedge, o);
    paintTyreWall(l.x, l.y + l.h, l.x + l.w, l.y + l.h, decor?.hedge, o);
    paintTyreWall(l.x, l.y, l.x, l.y + l.h, decor?.hedge, o);
    paintTyreWall(l.x + l.w, l.y, l.x + l.w, l.y + l.h, decor?.hedge, o);
  }
  const g = decor?.gateAt;
  if (g && !isPlan()) paintUnit(gantryUnit(g, decor?.gate, lookOf(P)), P, o);
}

/** D331: the gantry over the gate: two posts and a beam, its lights, flags or arch. */
export function gantryUnit(
  g: { x: number; y: number; px: number; py: number },
  look: string | undefined,
  L: Look,
): Unit {
  const G = GANTRIES[look ?? ''] ?? (GANTRIES.gantry_plain as (typeof GANTRIES)[string]);
  const along: [number, number] = [g.x - g.px, g.y - g.py];
  const len = Math.hypot(along[0], along[1]) || 1;
  const side: [number, number] = [-along[1] / len, along[0] / len];
  const pieces: Piece[] = [];
  const top = 0.85;
  const ends: Array<[number, number]> = [
    [g.x + side[0] * 0.6, g.y + side[1] * 0.6],
    [g.x - side[0] * 0.6, g.y - side[1] * 0.6],
  ];
  for (const [px, py] of ends)
    pieces.push({ s: 'box', x: px - 0.05, y: py - 0.05, w: 0.1, d: 0.1, z: 0, h: top, c: G.c });
  const [a, b] = ends as [[number, number], [number, number]];
  pieces.push({ s: 'rod', a: [a[0], a[1], top], b: [b[0], b[1], top], t: 0.12, c: G.c });
  pieces.push({
    s: 'rod',
    a: [a[0], a[1], top - 0.12],
    b: [b[0], b[1], top - 0.12],
    t: 0.06,
    c: M.team,
  });
  if (G.lights)
    for (let i = 0; i < 5; i++) {
      const f = 0.25 + i * 0.125;
      pieces.push({
        s: 'cyl',
        x: a[0] + (b[0] - a[0]) * f,
        y: a[1] + (b[1] - a[1]) * f,
        r: 0.035,
        z: top + 0.07,
        h: 0.04,
        c: L.night || i < 2 ? C.red : '#5A2A2A',
        glow: true,
      });
    }
  if (G.flags)
    for (const [px, py] of ends)
      pieces.push(
        { s: 'rod', a: [px, py, top], b: [px, py, top + 0.35], t: 0.02, c: M.steel },
        { s: 'box', x: px, y: py - 0.01, w: 0.16, d: 0.02, z: top + 0.22, h: 0.12, c: M.flash },
      );
  return { x: g.x - 0.6, y: g.y - 0.6, w: 1.2, d: 1.2, pieces };
}

/* ---------- D422: the asset module's plots, heart and fill, as both renderers place them ---------- */

// D440: the theme's accent and money colours over the game's palette, as the 3D map takes them.
const A = { ...ASSET_PALETTE, ...THEME_MAP_COLOURS.motorsport };

/**
 * D422, D424: where a piece from the asset module stands, carried on a placeholder unit so the
 * flat map paints simple shapes there and the 3D map builds the module's own piece in its place:
 * a producer's hero (`i` its index, `level` and `units`) on its plot, or the seeded paddock fill
 * on bare land.
 */
export type AssetPlace =
  | {
      kind: 'hero';
      i: number;
      level: number;
      units: number;
      x: number;
      y: number;
      w: number;
      h: number;
    }
  | { kind: 'fill'; seed: number; x: number; y: number; w: number; h: number };

/** A unit that marks a piece of the asset module. */
export type AssetUnit = Unit & { asset?: AssetPlace };

/** A box piece by its middle, for the shapes below. */
function boxAt(
  cx: number,
  cy: number,
  w: number,
  d: number,
  z: number,
  h: number,
  c: string,
  extra: { top?: string; glow?: boolean; roof?: boolean } = {},
): Piece {
  return { s: 'box', x: cx - w / 2, y: cy - d / 2, w, d, z, h, c, roof: true, ...extra };
}

/** The producer's hero as the flat map paints it: its main shed and its milestone pieces. */
function heroUnit(p: IsoParcel, b: Bounds): AssetUnit | null {
  const content = getIdleContent('motorsport');
  const i = content.producers.findIndex((q) => q.id === p.id);
  const prod = content.producers[i];
  if (!prod) return null;
  const units = p.owned ?? 0;
  const steps = prod.milestones.map((m) => m.owned);
  const level = assetLevel(units, steps);
  const cx = b.x + b.w / 2,
    cy = b.y + b.h / 2;
  const sc = ([0.66, 0.82, 1][Math.min(2, level)] ?? 1) * 1.08;
  const ly = levelHeight(level, steps.length);
  const pieces: Piece[] = [
    boxAt(cx, cy - 0.15 * sc, 2.0 * sc, 1.3 * sc, 0, 0.7 * ly, A.shedWall, { top: A.shedRoof }),
    boxAt(cx, cy + 0.52 * sc, 2.0 * sc, 0.04, 0.4 * ly, 0.08, A.team),
  ];
  if (units >= 10) pieces.push(boxAt(cx - 1.05, cy - 1.1, 0.26, 0.75, 0.06, 0.34, A.team));
  if (units >= 25)
    pieces.push(boxAt(cx - 0.95, cy + 0.95, 0.8, 0.5, 0, 0.5, A.shedWall, { top: A.team }));
  if (units >= 50)
    pieces.push(boxAt(cx - 1.05, cy - 0.1, 0.9, 0.6, 0, 1.0, A.shedWall, { top: A.shedRoof }));
  if (units >= 100)
    pieces.push(
      boxAt(cx + 1.2, cy - 0.1, 0.08, 0.08, 0, 2.4, A.metal, { roof: false }),
      boxAt(cx + 1.2, cy - 0.1, 0.5, 0.18, 2.4, 0.18, A.lampWarm, { glow: true, roof: false }),
    );
  return {
    x: b.x,
    y: b.y,
    w: b.w,
    d: b.h,
    pieces,
    id: p.id,
    asset: { kind: 'hero', i, level, units, x: b.x, y: b.y, w: b.w, h: b.h },
  };
}

/** The module's seeded random numbers (Park and Miller), so both maps draw the same fill. */
function parkMiller(seed: number): () => number {
  let s = seed;
  return () => ((s = (s * 16807) % 2147483647) - 1) / 2147483646;
}

/**
 * D422: the paddock fill on a free `w` by `d` rectangle, as the module's `motorsportPaddock`
 * places it from the same seed: canopies, tyre stacks, transporters and mechanics on tarmac.
 */
function fillUnit(x: number, y: number, w: number, d: number, seed: number): AssetUnit {
  const r = parkMiller(seed);
  const pieces: Piece[] = [{ s: 'box', x, y, w, d, z: 0, h: 0.02, c: A.tarmacLight }];
  const items = Math.max(2, Math.round((w * d) / 2.5));
  for (let i = 0; i < items; i++) {
    const px = x + 0.4 + r() * (w - 0.8),
      py = y + 0.4 + r() * (d - 0.8),
      k = r();
    if (k < 0.35) {
      const c = r() < 0.5 ? A.white : A.team;
      pieces.push(boxAt(px, py, 0.8, 0.6, 0.4, 0.05, c));
      for (const [ox, oy] of [
        [-0.37, -0.27],
        [0.37, -0.27],
        [-0.37, 0.27],
        [0.37, 0.27],
      ] as const)
        pieces.push(boxAt(px + ox, py + oy, 0.03, 0.03, 0, 0.4, A.metal, { roof: false }));
    } else if (k < 0.6) {
      pieces.push({ s: 'cyl', x: px, y: py, r: 0.1, z: 0, h: 0.27, c: A.tyre });
      pieces.push({ s: 'cyl', x: px + 0.24, y: py, r: 0.1, z: 0, h: 0.36, c: A.tyre });
    } else if (k < 0.8) {
      pieces.push(boxAt(px, py, 1.1, 0.34, 0.05, 0.42, A.team));
      pieces.push(boxAt(px + 0.68, py, 0.3, 0.34, 0.05, 0.32, A.white));
    } else pieces.push(boxAt(px, py, 0.1, 0.08, 0, 0.26, A.team, { roof: false }));
  }
  return { x, y, w, d, pieces, asset: { kind: 'fill', seed, x, y, w, h: d } };
}

/** A seed for a rectangle of fill from where it lies: always the same, never 0. */
function fillSeed(x: number, y: number): number {
  return 1 + ((((x + 64) * 7919 + (y + 64) * 104729) >>> 0) % 2147483640);
}

/**
 * D422: bare land broken into rectangles, back row first, each as wide then as deep as the free
 * tiles allow; those two by two or larger take the paddock fill, the tiles left keep their trees.
 */
function fillRects(tiles: readonly string[]): { rects: Bounds[]; left: string[] } {
  const free = new Set(tiles);
  const order = [...tiles]
    .map((k) => k.split(',').map(Number) as [number, number])
    .sort((a, b) => a[1] - b[1] || a[0] - b[0]);
  const rects: Bounds[] = [];
  for (const [x, y] of order) {
    if (!free.has(`${x},${y}`)) continue;
    let w = 1;
    while (free.has(`${x + w},${y}`)) w++;
    let h = 1;
    while (Array.from({ length: w }, (_, i) => free.has(`${x + i},${y + h}`)).every(Boolean)) h++;
    if (w < 2 || h < 2) continue;
    for (let j = 0; j < h; j++) for (let i = 0; i < w; i++) free.delete(`${x + i},${y + j}`);
    rects.push({ x, y, w, h });
  }
  return { rects, left: tiles.filter((k) => free.has(k)) };
}

/** D422: the heart's stage from its drivers, as the module reads them. */
export function heartStage(h: Heart): number {
  const byP = h.producers >= 15 ? 2 : h.producers >= 7 ? 1 : 0;
  const byY = h.years >= 7 ? 2 : h.years >= 3 ? 1 : 0;
  return Math.max(byP, byY);
}

/**
 * D422, D423: the heart as the flat map paints it, from the same plan as the 3D map's: the pad
 * and pit lane, the pit building, race control, the stand from the second stage and its roof,
 * screen and floodlights at the third. Laid out on the module's 7 by 7 plot and scaled evenly
 * onto the yard, mirrored with the world.
 */
function heartUnit(h: Heart): Unit {
  const plan = heartPlan(h);
  const k = plan.size / 7,
    m = h.dir;
  const st = heartStage(h);
  const box = (
    lx: number,
    ly: number,
    w: number,
    d: number,
    z: number,
    hh: number,
    c: string,
    extra: { top?: string; glow?: boolean; roof?: boolean } = {},
  ): Piece =>
    boxAt(
      plan.x + (3.5 + m * lx) * k,
      plan.y + (3.5 + ly) * k,
      w * k,
      d * k,
      z * k,
      hh * k,
      c,
      extra,
    );
  const shedW = [3.6, 5.0, 6.0][st] ?? 3.6;
  const pieces: Piece[] = [
    box(0, 0, 7, 7, 0, 0.03, A.tarmac, { roof: false }),
    box(0, 2.5, 7, 0.9, 0.03, 0.01, A.tarmacLight, { roof: false }),
    box(0, 1.2, shedW, 1.4, 0, [0.8, 0.9, 1.0][st] ?? 0.8, A.shedWall, {
      top: A.shedRoof,
    }),
    box(0, 1.9, shedW, 0.04, 0.6, 0.1, A.team),
    box(-2.4, -1.0, 1.6, 0.6, 0, 0.8, A.white, { top: A.window }),
  ];
  if (st >= 1) pieces.push(box(0.6, -1.85, 3.0, 1.2, 0, 0.88, A.team, { top: A.white }));
  if (st >= 2) {
    pieces.push(box(0.4, -2.45, 6.0, 1.8, 0, 1.76, A.white, { top: A.team }));
    pieces.push(box(0.4, -2.2, 6.2, 2.0, 2.3, 0.08, A.shedRoof));
    pieces.push(box(2.6, -3.2, 2.2, 0.1, 2.4, 1.2, A.window, { roof: false }));
    for (const [x, y] of [
      [-3.3, 3.3],
      [3.3, 3.3],
      [-3.3, -3.3],
      [3.3, -3.3],
    ] as const)
      pieces.push(
        box(x, y, 0.08, 0.08, 0, 3.6, A.metal, { roof: false }),
        box(x, y, 0.5, 0.2, 3.6, 0.2, A.lampWarm, { glow: true, roof: false }),
      );
  }
  return { x: plan.x, y: plan.y, w: plan.size, d: plan.size, pieces };
}

/**
 * D423: the store's bays beside the heart, one for every storage upgrade, drawn the same in both
 * maps: a garage each, its door to the front, a band in the team's colour.
 */
export function storeUnits(h: Heart): Unit[] {
  return heartPlan(h).bays.map((b) => ({
    x: b.x,
    y: b.y,
    w: b.w,
    d: b.h,
    pieces: [
      {
        s: 'box',
        x: b.x,
        y: b.y,
        w: b.w,
        d: b.h,
        z: 0,
        h: 0.62,
        c: A.shedWall,
        top: A.shedRoof,
        roof: true,
      },
      {
        s: 'box',
        x: b.x + b.w * 0.15,
        y: b.y + b.h - 0.02,
        w: b.w * 0.7,
        d: 0.03,
        z: 0,
        h: 0.46,
        c: A.window,
      },
      { s: 'box', x: b.x, y: b.y + b.h - 0.03, w: b.w, d: 0.04, z: 0.5, h: 0.07, c: A.team },
    ],
  }));
}

/**
 * D422, D423: a building's units, the heart's in place of the home's: the 3D map builds the
 * module's heart and takes only the store's bays from here (`flat` off); the flat map paints both.
 */
export function motorsportBuildingUnits(b: IsoBuildingAt, flat = true): Unit[] {
  if (!b.heart) return buildingUnits(b);
  return flat ? [heartUnit(b.heart), ...storeUnits(b.heart)] : storeUnits(b.heart);
}

/** D422: each plot's own edge on the flat map: its tarmac pad and its red and white kerb. */
function paintPlotEdges(parcels: IsoParcel[], _P: Palette, o: string[]): void {
  if (isPlan()) return;
  for (const p of parcels) {
    if (p.t !== 'field' || !p.id || !p.tiles.length) continue;
    const b = unionBounds(p.tiles);
    o.push(`<path d="${polyPath(rectPts(b.x, b.y, b.w, b.h))}" fill="${A.tarmac}"/>`);
    const t = 0.14;
    const run = (x0: number, y0: number, len: number, along: boolean) => {
      const n = Math.max(1, Math.round(len / 0.5));
      for (let i = 0; i < n; i++) {
        const a = (len * i) / n,
          e = (len * (i + 1)) / n;
        const pts = along ? rectPts(x0 + a, y0, e - a, t) : rectPts(x0, y0 + a, t, e - a);
        o.push(`<path d="${polyPath(pts)}" fill="${i % 2 ? A.kerbWhite : A.kerbRed}"/>`);
      }
    };
    run(b.x, b.y, b.w, true);
    run(b.x, b.y + b.h - t, b.w, true);
    run(b.x, b.y, b.h, false);
    run(b.x + b.w - t, b.y, b.h, false);
  }
}

/* ---------- D437: the circuit in the margin, wrapping the paddock on two sides ---------- */

/**
 * D437: the circuit's measures, in tiles. Its spine runs along each side it takes, `gap` plus
 * `half` out from the fence; the tarmac's outer edge is `half` from the spine and its inner
 * edge `tarmac` nearer, the infield between; the verge runs `verge` beyond the tarmac; and the
 * grand prix's grandstand stands within `stand` beyond the verge, on the side away from the land.
 */
export const CIRCUIT = { gap: 0.4, half: 1.15, tarmac: 0.6, verge: 0.3, stand: 1.6 } as const;
/** The inside bend turns round the land's own corner: its centre, from the spine's corner. */
const BEND = CIRCUIT.gap + CIRCUIT.half;
/** The racing line: the middle of the tarmac. */
const LINE = CIRCUIT.half - CIRCUIT.tarmac / 2;
/** The spine to the verge's outer edge. */
const REACH = CIRCUIT.half + CIRCUIT.verge;
/** The second side is left off when its spine would be shorter than this. */
const SIDE_MIN = BEND + 0.5;

/**
 * D437: where the circuit may run. `(cx, cy)` is the corner of the land drawn (the camera's
 * frame, D259) across from the gate, `sx`
 * and `sy` point out of the land there; `a` is how long the spine may run back along the side
 * across x (to the land's far end at most) and `b` along the side across y, each stopping short
 * of the gate's road, the lane, the river and every piece of land beside the world. `b` is 0
 * when the second side has no room.
 */
export interface CircuitSite {
  cx: number;
  cy: number;
  sx: 1 | -1;
  sy: 1 | -1;
  a: number;
  b: number;
}

/** D437: the circuit's site in the margin from the seeded layout, or null when it has no room. */
export function circuitSite(L: WorldLayout, plot: Bounds, road: Bounds): CircuitSite | null {
  // The sides are the world's own, away from the gate; the fence is the land drawn (D259), so
  // the circuit moves out as the land grows.
  const sx = L.gate.x < L.width / 2 ? 1 : -1;
  const sy = L.gate.y < L.height / 2 ? 1 : -1;
  // D440: when the lane or the road runs along the side across x there (the default plan's
  // deeper yard sends the lane out along the fresh land's edge), the far side across y is taken.
  return siteAt(L, plot, road, sx, sy) ?? siteAt(L, plot, road, sx, sy > 0 ? -1 : 1);
}

function siteAt(
  L: WorldLayout,
  plot: Bounds,
  road: Bounds,
  sx: 1 | -1,
  sy: 1 | -1,
): CircuitSite | null {
  const cx = sx > 0 ? plot.x + plot.w : plot.x,
    cy = sy > 0 ? plot.y + plot.h : plot.y;
  const lane: Bounds[] = [];
  for (let i = 1; i < L.lane.length; i++) {
    const p = L.lane[i - 1] as { x: number; y: number },
      q = L.lane[i] as { x: number; y: number };
    const x = Math.min(p.x, q.x),
      y = Math.min(p.y, q.y);
    lane.push(pad({ x, y, w: Math.abs(q.x - p.x), h: Math.abs(q.y - p.y) }, 0.5));
  }
  const blocks: Bounds[] = [
    pad(road, 0.5),
    ...lane,
    // Every piece of land beside the world not already drawn as part of the land.
    ...Object.values(L.lands)
      .map((l) => l.bounds)
      .filter((b) => !overlaps(b, plot))
      .map((b) => pad(b, 0.5)),
    ...L.river.map((t) => pad({ x: t.x, y: t.y, w: 1, h: 1 }, 0.3)),
  ];
  const off = CIRCUIT.gap + CIRCUIT.half;
  const local = (b: Bounds) => {
    const xs = [(b.x - cx) * sx - off, (b.x + b.w - cx) * sx - off];
    const zs = [(b.y - cy) * sy - off, (b.y + b.h - cy) * sy - off];
    return { x0: Math.min(...xs), x1: Math.max(...xs), z0: Math.min(...zs), z1: Math.max(...zs) };
  };
  let a = plot.w + CIRCUIT.gap - CIRCUIT.verge;
  let b = plot.h + CIRCUIT.gap - CIRCUIT.verge;
  for (const blk of blocks.map(local)) {
    // Across the side along x, the grandstand's room included.
    // Only what lies along the side itself counts, not what lies beyond the corner.
    if (blk.z1 > -REACH && blk.z0 < REACH + CIRCUIT.stand && blk.x0 < REACH)
      a = blk.x1 >= -REACH ? 0 : Math.min(a, -blk.x1 - REACH);
    if (blk.x1 > -REACH && blk.x0 < REACH && blk.z0 < REACH)
      b = blk.z1 >= -REACH ? 0 : Math.min(b, -blk.z1 - REACH);
  }
  if (a < BEND + 1) return null;
  return { cx, cy, sx, sy, a, b: b >= SIDE_MIN ? b : 0 };
}

/** The site as the circuit's country items carry it (`k`). */
function siteKey(s: CircuitSite): string {
  return [s.cx, s.cy, s.sx, s.sy, s.a.toFixed(2), s.b.toFixed(2)].join(',');
}

/** D437: the site the circuit's country items carry, or null for a world with none. */
export function circuitSiteOf(country: readonly CountryItem[]): CircuitSite | null {
  const k = country.find((q) => q.t === 'mcircuit')?.k;
  const n = k?.split(',').map(Number);
  if (!n || n.length !== 6 || n.some((v) => !Number.isFinite(v))) return null;
  const [cx, cy, sx, sy, a, b] = n as [number, number, number, number, number, number];
  return { cx, cy, sx: sx < 0 ? -1 : 1, sy: sy < 0 ? -1 : 1, a, b };
}

/**
 * D437: the circuit's country items: the ground the grand prix layout takes on each side, kept
 * clear of trees and laid flat whatever the form, so the circuit lengthening never meets them.
 */
function circuitItems(s: CircuitSite): CountryItem[] {
  const k = siteKey(s);
  const box = (x0: number, x1: number, z0: number, z1: number, r: number): CountryItem => {
    const [ax, ay] = siteToWorld(s, x0, z0),
      [bx, by] = siteToWorld(s, x1, z1);
    const x = Math.min(ax, bx),
      y = Math.min(ay, by);
    return { t: 'mcircuit', x, y, w: Math.abs(bx - ax), h: Math.abs(by - ay), r, k };
  };
  const out = [box(-s.a - REACH, REACH, -REACH, REACH + CIRCUIT.stand, 1)];
  if (s.b) out.push(box(-REACH, REACH, -s.b - REACH, REACH, 0));
  return out;
}

/** A point in the circuit's own frame (the spine's corner at 0, the land toward -x and -z). */
function siteToWorld(s: CircuitSite, lx: number, lz: number): [number, number] {
  const off = CIRCUIT.gap + CIRCUIT.half;
  return [s.cx + s.sx * (off + lx), s.cy + s.sy * (off + lz)];
}

/** D437: the heart's form for the circuit, from the producers running and the years held. */
export function circuitForm(parcels: readonly IsoParcel[], years: number): 0 | 1 | 2 {
  return heartForm(parcels.filter((p) => p.t === 'field' && p.id).length, years);
}

/** A box of the circuit's, by its middle, in the asset palette's colours (`A`). */
export interface CircuitBox {
  x: number;
  y: number;
  w: number;
  d: number;
  z: number;
  h: number;
  key: keyof typeof A;
  glow?: boolean;
  /** Part of the grandstand: the 3D map builds the module's own in its place. */
  stand?: boolean;
}

/** A red or white kerb block on a bend: its middle and the way it runs, in the world. */
export interface CircuitKerb {
  x: number;
  y: number;
  ux: number;
  uy: number;
  red: boolean;
}

/**
 * D437: the circuit at a form, as both maps draw it. Form 0 is a club loop along the side
 * across x; form 1 a national circuit round the corner and half way along the side across y,
 * with kerbs on its bends, its start gantry and a pit wall facing the paddock; form 2 the grand
 * prix layout the full length of both sides, its grandstand and giant screen by the start line.
 */
export interface CircuitPlan {
  site: CircuitSite;
  form: 0 | 1 | 2;
  /** The closed loop `d` from the spine (the tarmac's outer edge at `CIRCUIT.half`), in the world. */
  ring: (d: number) => Array<[number, number]>;
  /** The racing line, in the world. */
  line: Array<[number, number]>;
  /** The start line across the tarmac, its four corners. */
  start: Array<[number, number]>;
  kerbs: CircuitKerb[];
  boxes: CircuitBox[];
  /** The grandstand (form 2): its middle at the front row, its length, and which way it faces. */
  stand: { x: number; y: number; len: number; faceY: 1 | -1 } | null;
}

/** D437: the circuit for a world's country at `form`, or null for a world with none. */
export function circuitPlan(country: readonly CountryItem[], form: 0 | 1 | 2): CircuitPlan | null {
  const site = circuitSiteOf(country);
  if (!site) return null;
  const a = form === 0 ? Math.min(site.a, Math.max(BEND + 2, site.a * 0.5)) : site.a;
  const b2 =
    form === 0 ? 0 : form === 1 ? Math.min(site.b, Math.max(SIDE_MIN, site.b * 0.5)) : site.b;
  const b = b2 >= SIDE_MIN ? b2 : 0;
  const W = (lx: number, lz: number) => siteToWorld(site, lx, lz);
  const arc = (
    out: Array<[number, number]>,
    cx: number,
    cz: number,
    r: number,
    t0: number,
    t1: number,
  ) => {
    const n = Math.max(3, Math.ceil(Math.abs(t1 - t0) / (Math.PI / 16)));
    for (let i = 0; i <= n; i++) {
      const t = t0 + ((t1 - t0) * i) / n;
      out.push([cx + Math.cos(t) * r, cz + Math.sin(t) * r]);
    }
  };
  const P = Math.PI;
  /** The loop `d` from the spine, in the circuit's own frame. */
  const loop = (d: number): Array<[number, number]> => {
    const o: Array<[number, number]> = [];
    if (b) {
      o.push([-a, d], [0, d]);
      arc(o, 0, 0, d, P / 2, 0);
      o.push([d, -b]);
      arc(o, 0, -b, d, 0, -P);
      o.push([-d, -BEND]);
      arc(o, -BEND, -BEND, BEND - d, 0, P / 2);
      o.push([-a, -d]);
    } else {
      o.push([-a, d], [0, d]);
      arc(o, 0, 0, d, P / 2, -P / 2);
      o.push([-a, -d]);
    }
    arc(o, -a, 0, d, -P / 2, (-3 * P) / 2);
    // Drop the points that repeat where a straight meets a bend.
    return o.filter((p, i) => {
      const q = o[(i + 1) % o.length] as [number, number];
      return Math.hypot(p[0] - q[0], p[1] - q[1]) > 1e-6;
    });
  };
  const ring = (d: number) => loop(d).map(([x, z]) => W(x, z));
  // The start line, half way along the inner straight beside the land across x.
  const xs = b ? -(a + BEND) / 2 : -a / 2;
  const zIn = -CIRCUIT.half,
    zOut = -CIRCUIT.half + CIRCUIT.tarmac;
  const start = [W(xs - 0.05, zIn), W(xs + 0.05, zIn), W(xs + 0.05, zOut), W(xs - 0.05, zOut)];
  // Kerbs on the outside of every bend, from form 1.
  const kerbs: CircuitKerb[] = [];
  const kerbRun = (cx: number, cz: number, r: number, t0: number, t1: number) => {
    const n = Math.max(2, Math.round((Math.abs(t1 - t0) * r) / 0.3));
    for (let i = 0; i < n; i++) {
      const t = t0 + ((t1 - t0) * (i + 0.5)) / n;
      const [x, y] = W(cx + Math.cos(t) * r, cz + Math.sin(t) * r);
      const [x2, y2] = W(cx + Math.cos(t + 0.01) * r, cz + Math.sin(t + 0.01) * r);
      const len = Math.hypot(x2 - x, y2 - y) || 1;
      kerbs.push({ x, y, ux: (x2 - x) / len, uy: (y2 - y) / len, red: i % 2 === 0 });
    }
  };
  if (form >= 1) {
    const edge = CIRCUIT.half - 0.05;
    kerbRun(-a, 0, edge, -P / 2, (-3 * P) / 2);
    if (b) {
      kerbRun(0, 0, edge, P / 2, 0);
      kerbRun(0, -b, edge, 0, -P);
      kerbRun(-BEND, -BEND, BEND - (CIRCUIT.half - CIRCUIT.tarmac) - 0.05, 0, P / 2);
    } else kerbRun(0, 0, edge, P / 2, -P / 2);
  }
  const boxes: CircuitBox[] = [];
  const put = (
    lx: number,
    lz: number,
    w: number,
    d: number,
    z: number,
    h: number,
    key: CircuitBox['key'],
    more: { glow?: boolean; stand?: boolean } = {},
  ) => {
    const [x, y] = W(lx, lz);
    boxes.push({ x, y, w, d, z, h, key, ...more });
  };
  // The start line itself, white across the tarmac.
  put(xs, (zIn + zOut) / 2, 0.1, CIRCUIT.tarmac, 0.02, 0.004, 'white');
  let stand: CircuitPlan['stand'] = null;
  if (form >= 1) {
    // The start gantry over the line, and the pit wall on the fence's side of it, facing the
    // paddock, with its timing boards.
    const fence = zIn - 0.15;
    for (const z of [fence, zOut + 0.1]) put(xs, z, 0.06, 0.06, 0, 0.95, 'metal');
    put(xs, (fence + zOut + 0.1) / 2, 0.08, zOut + 0.1 - fence, 0.95, 0.1, 'kerbRed');
    const wall = Math.min(5, a * 0.45);
    put(xs, fence, wall, 0.07, 0, 0.16, 'white');
    put(xs, fence, wall + 0.01, 0.075, 0.16, 0.03, 'team');
    for (const f of [-0.35, 0, 0.35]) {
      const x = xs + f * wall + 0.3;
      put(x, fence - 0.02, 0.04, 0.04, 0, 0.42, 'metal');
      put(x, fence - 0.05, 0.24, 0.03, 0.3, 0.14, 'screen', { glow: true });
    }
  }
  if (form >= 2) {
    // The main grandstand outside the verge by the start line, facing the track and the land,
    // its rows rising away; the giant screen beside it on its two posts.
    const len = Math.min(4, a * 0.35);
    const z0 = CIRCUIT.half + CIRCUIT.verge + 0.3;
    for (let i = 0; i < 4; i++)
      put(xs, z0 + i * 0.3, len, 0.3, 0, (i + 1) * 0.22, i % 2 ? 'white' : 'team', {
        stand: true,
      });
    put(xs, z0 + 0.5, len * 1.03, 1.3, 1.3, 0.06, 'shedRoof', { stand: true });
    const [x, y] = W(xs, z0);
    stand = { x, y, len, faceY: -site.sy as 1 | -1 };
    const sx = xs - len / 2 - 1.0,
      sz = z0 + 0.2;
    for (const f of [-0.6, 0.6]) put(sx + f, sz, 0.08, 0.08, 0, 1.15, 'metal');
    put(sx, sz, 1.5, 0.08, 1.1, 0.85, 'window');
    put(sx, sz - 0.06, 1.4, 0.03, 1.15, 0.75, 'screen', { glow: true });
  }
  return { site, form, ring, line: ring(LINE), start, kerbs, boxes, stand };
}

/** D437: the racing line at `form`, in the world, for the cars that lap it. */
export function circuitLine(
  country: readonly CountryItem[],
  form: 0 | 1 | 2,
): Array<[number, number]> | null {
  return circuitPlan(country, form)?.line ?? null;
}

/** One thing of the module's country, where the module's `scenery` puts it. */
export type SceneryItem =
  | { t: 'tree'; x: number; z: number; s: number; pine: boolean }
  | { t: 'park'; x: number; z: number }
  | { t: 'tent'; x: number; z: number; r: number; flag: boolean }
  | { t: 'tower'; x: number; z: number }
  | { t: 'turbine'; x: number; z: number };

/**
 * D422, D426: the module's country for motorsport (`scenery('motorsport', …)`), worked out
 * without three.js so the flat map paints the same things in the same places as the 3D map
 * builds: the module's seeded draws and its rolling ground's height test, step for step. `seed`
 * and `margin` are what the 3D map passes the module; `inside` keeps the land, the pieces of land
 * beside it and the circuit, road, car park and town the layout placed clear, in both maps.
 */
export function sceneryPlan(
  plot: Bounds,
  country: readonly CountryItem[],
  lands: readonly Bounds[],
  progress: number,
): {
  seed: number;
  margin: number;
  inside: (x: number, z: number) => boolean;
  items: SceneryItem[];
} {
  const c = country.find((q) => q.t === 'mcircuit');
  const seed = 1 + (Math.abs(Math.round((c?.x ?? 0) * 31 + (c?.y ?? 0) * 17)) % 9973);
  const margin = LANDSCAPE.haze.far / 2;
  const grow = (b: Bounds, n: number): Bounds => ({
    x: b.x - n,
    y: b.y - n,
    w: b.w + 2 * n,
    h: b.h + 2 * n,
  });
  const keep: Bounds[] = [grow(plot, 1), ...lands.map((b) => grow(b, 1))];
  const pad: Record<string, number> = { mcircuit: 1.2, mroad: 0.8, mpark: 0.5, mhouse: 0.3 };
  for (const q of country) if (pad[q.t] !== undefined) keep.push(grow(q, pad[q.t] as number));
  const inside = (x: number, z: number) =>
    keep.some((b) => x >= b.x && x <= b.x + b.w && z >= b.y && z <= b.y + b.h);
  // The module's own noise and draws (`hash3`, `vnoise`, `fbm`, `rng`), as it computes them.
  const hash3 = (x: number, y: number, z: number) => {
    const h = Math.sin(x * 127.1 + y * 311.7 + z * 74.7) * 43758.5453;
    return h - Math.floor(h);
  };
  const vnoise = (x: number, y: number, z: number) => {
    const xi = Math.floor(x),
      yi = Math.floor(y),
      zi = Math.floor(z);
    const xf = x - xi,
      yf = y - yi,
      zf = z - zi;
    const u = xf * xf * (3 - 2 * xf),
      v = yf * yf * (3 - 2 * yf),
      w = zf * zf * (3 - 2 * zf);
    const L = (a: number, b: number, t: number) => a + (b - a) * t;
    const H = (i: number, j: number, k: number) => hash3(xi + i, yi + j, zi + k);
    return L(
      L(L(H(0, 0, 0), H(1, 0, 0), u), L(H(0, 1, 0), H(1, 1, 0), u), v),
      L(L(H(0, 0, 1), H(1, 0, 1), u), L(H(0, 1, 1), H(1, 1, 1), u), v),
      w,
    );
  };
  const fbm = (x: number, y: number, z: number) =>
    vnoise(x, y, z) * 0.55 +
    vnoise(x * 2.1, y * 2.1, z * 2.1) * 0.3 +
    vnoise(x * 4.7, y * 4.7, z * 4.7) * 0.15;
  const r = parkMiller(seed);
  const x0 = plot.x,
    z0 = plot.y,
    x1 = plot.x + plot.w,
    z1 = plot.y + plot.h;
  const X0 = x0 - margin,
    Z0 = z0 - margin,
    X1 = x1 + margin,
    Z1 = z1 + margin;
  const outside = (x: number, z: number) =>
    Math.hypot(Math.max(x0 - x, 0, x - x1), Math.max(z0 - z, 0, z - z1));
  const sm = (a: number, b: number, t: number) => {
    const k = Math.max(0, Math.min(1, (t - a) / (b - a)));
    return k * k * (3 - 2 * k);
  };
  const heightAt = (x: number, z: number) =>
    Math.max(0, (fbm(x * 0.11 + seed, 0.5, z * 0.11) - 0.3) * 1.2 * 2) * sm(2.5, 9, outside(x, z));
  const inLand = (x: number, z: number, p: number) =>
    x > x0 - p && x < x1 + p && z > z0 - p && z < z1 + p;
  const items: SceneryItem[] = [];
  const cell = 3;
  for (let x = X0; x < X1; x += cell)
    for (let z = Z0; z < Z1; z += cell) {
      const cx = x + cell / 2,
        cz = z + cell / 2;
      if (inLand(cx, cz, cell * 0.6)) continue;
      const k = r();
      if (k < 0.35)
        for (let i = 0; i < 4; i++) {
          const tx = cx - 1 + r() * 2,
            tz = cz - 1 + r() * 2,
            s = 1 + r() * 0.6;
          items.push({
            t: 'tree',
            x: tx,
            z: tz,
            s,
            pine: (((tx * 7.3 + tz * 3.1) % 1) + 1) % 1 < 0.35,
          });
        }
      else if (k < 0.5 && heightAt(cx, cz) < 0.08) items.push({ t: 'park', x: cx, z: cz });
      else if (k < 0.6)
        items.push(
          { t: 'tent', x: cx, z: cz, r: 0.35, flag: true },
          { t: 'tent', x: cx + 0.8, z: cz + 0.3, r: 0.3, flag: false },
        );
    }
  if (progress > 0.7) items.push({ t: 'tower', x: x1 + 1.2, z: z0 + 4.2 });
  for (let i = 0; i < 4; i++) items.push({ t: 'turbine', x: X0 + 4 + i * 4, z: Z0 + 1.5 });
  return { seed, margin, inside, items: items.filter((q) => !inside(q.x, q.z)) };
}

/** D426: how far along the motorsport world is, for the module's country: producers running. */
export function sceneryProgress(parcels: readonly IsoParcel[]): number {
  const running = parcels.filter((p) => p.t === 'field' && p.id).length;
  return Math.min(1, running / Math.max(1, getIdleContent('motorsport').producers.length));
}

/**
 * D426: the dyno rooms' exhaust stacks, read off the module's hero as it builds them: three to
 * five cells across the plot, a stack at the back of each, scaled and raised with the hero.
 * Each top is `[x, y, height]`, `y` the map's depth.
 */
export function dynoTops(units: readonly Unit[]): Array<[number, number, number]> {
  const out: Array<[number, number, number]> = [];
  for (const u of units) {
    const a = (u as AssetUnit).asset;
    if (a?.kind !== 'hero' || u.id !== 'dyno_rooms') continue;
    const U = a.units;
    const L =
      a.level <= 1
        ? a.level
        : U >= 500
          ? 8
          : U >= 250
            ? 7
            : U >= 100
              ? 6
              : U >= 50
                ? 5
                : U >= 25
                  ? 4
                  : U >= 10
                    ? 3
                    : 2;
    const n = L >= 4 ? 5 : 3 + Math.min(Math.min(4, L), 1);
    const sc = ([0.66, 0.82][L] ?? 1) * 1.08;
    const ly = levelHeight(a.level);
    const ox = a.x + (a.w - 3) / 2 + 1.5,
      oz = a.y + (a.h - 3) / 2 + 1.5;
    for (let i = 0; i < n; i++) {
      const x = -1 + i * (2 / (n - 1));
      out.push([ox + (x + 0.12) * sc, oz - 0.6 * sc, 1.68 * sc * ly]);
    }
  }
  return out;
}

/** D437: the circuit on the flat map: grass verge, tarmac loop, infield, start line and kerbs. */
function paintCircuit(c: CircuitPlan, P: Palette, o: string[]): void {
  const grass = P.S.frost ? mix(A.grass, SNOW.colour, WINTER.grass) : A.grass;
  const loop = (d: number) => polyPath(c.ring(d).map(([x, y]) => project(x, y)));
  const { half, tarmac, verge } = CIRCUIT;
  o.push(`<path d="${loop(half + verge)}" fill="${grass}"/>`);
  o.push(`<path d="${loop(half)}" fill="${A.tarmac}"/>`);
  o.push(`<path d="${loop(half - tarmac)}" fill="${grass}"/>`);
  o.push(
    `<path d="${polyPath(c.ring(half - 0.04).map(([x, y]) => project(x, y)))}" fill="none" stroke="${alpha(A.kerbWhite, 0.7)}" stroke-width=".8"/>`,
  );
  o.push(`<path d="${polyPath(c.start.map(([x, y]) => project(x, y)))}" fill="${A.white}"/>`);
  for (const k of c.kerbs) {
    const [tx, ty] = [k.ux * 0.15, k.uy * 0.15],
      [nx, ny] = [-k.uy * 0.06, k.ux * 0.06];
    const pts: Array<[number, number]> = [
      [k.x - tx - nx, k.y - ty - ny],
      [k.x + tx - nx, k.y + ty - ny],
      [k.x + tx + nx, k.y + ty + ny],
      [k.x - tx + nx, k.y - ty + ny],
    ];
    o.push(
      `<path d="${polyPath(pts.map(([x, y]) => project(x, y)))}" fill="${k.red ? A.kerbRed : A.kerbWhite}"/>`,
    );
  }
}

/** D437: what stands on the circuit, as flat units: the gantry, the pit wall, the stand, the screen. */
function circuitUnits(c: CircuitPlan): Unit[] {
  return c.boxes
    .filter((q) => q.h > 0.01)
    .map((q) => ({
      x: q.x - q.w / 2,
      y: q.y - q.d / 2,
      w: q.w,
      d: q.d,
      pieces: [boxAt(q.x, q.y, q.w, q.d, q.z, q.h, A[q.key], q.glow ? { glow: true } : {})],
    }));
}

/** D426: one thing of the module's country as a flat unit, in the module's colours. */
function sceneryUnit(s: SceneryItem): Unit {
  const at = (w: number, pieces: Piece[]): Unit => ({
    x: s.x - w / 2,
    y: s.z - w / 2,
    w,
    d: w,
    pieces,
  });
  switch (s.t) {
    case 'tree':
      return at(0.6 * s.s, [
        { s: 'cyl', x: s.x, y: s.z, r: 0.05, z: 0, h: 0.3 * s.s, c: A.timberDark },
        {
          s: 'ball',
          x: s.x,
          y: s.z,
          r: (s.pine ? 0.22 : 0.32) * s.s,
          z: 0.2 * s.s,
          c: s.pine ? A.hedge : A.leaf,
        },
      ]);
    case 'park':
      return at(2.6, [
        boxAt(s.x, s.z, 2.6, 2.6, 0, 0.02, A.carPark),
        ...[0, 1, 2].map((i) =>
          boxAt(
            s.x - 0.8 + i * 0.7,
            s.z,
            0.18,
            0.34,
            0.02,
            0.12,
            [A.white, A.rival, A.rival2][i] ?? A.white,
          ),
        ),
      ]);
    case 'tent':
      return at(s.r * 2, [
        boxAt(s.x, s.z, s.r * 1.3, s.r * 1.3, 0, s.r * 1.2, A.canvas),
        ...(s.flag ? [boxAt(s.x + 0.08, s.z, 0.16, 0.02, s.r * 1.6, 0.1, A.team)] : []),
      ]);
    case 'tower':
      return at(1.4, [
        boxAt(s.x, s.z, 1.0, 1.0, 0, 3.2, A.white),
        boxAt(s.x, s.z, 1.4, 1.4, 3.2, 0.7, A.glass, { top: A.team }),
      ]);
    case 'turbine':
      return at(0.3, [
        { s: 'cyl', x: s.x, y: s.z, r: 0.08, z: 0, h: 3.4, c: A.white },
        boxAt(s.x + 0.1, s.z, 0.3, 0.16, 3.4, 0.16, A.white),
      ]);
  }
}

/* ---------- what stands on the land ---------- */

/** Everything standing on a parcel, placed for both renderers. */
export function motorsportProps(
  p: IsoParcel,
  showSigns: boolean,
  _L: Look = { night: false, winter: false },
): Prop[] {
  const out: Prop[] = [];
  const b = unionBounds(p.tiles);
  if (!b.w) return out;
  if (p.t === 'field' && p.id) {
    // D422: the producer's hero from the asset module (a placeholder the flat map paints simply).
    const hero = heroUnit(p, b);
    const units = hero ? [hero] : [];
    for (const u of units) out.push(piece(u));
    if (p.sign && showSigns) {
      const at = corner(b, 0, 0.12);
      out.push({ k: 'sign', x: at[0], y: at[1], text: p.sign });
    }
    // D268: a mechanic by the plot once a hand is hired, more with the milestones.
    const staff = (p.hand ? 1 : 0) + Math.min(2, Math.floor((p.tier ?? 0) / 5));
    for (let i = 0; i < staff; i++) {
      const at = corner(b, 3 - (i % 2) * 2, 0.08 + i * 0.12);
      out.push({ k: 'worker', x: at[0], y: at[1], ...(p.workers ? { look: p.workers } : {}) });
    }
    if (p.burst)
      out.push({ k: 'burst', x: b.x + b.w / 2, y: b.y + b.h / 2, r: Math.max(b.w, b.h) / 2 });
    if (p.ready) {
      const [fx, fy] = corner(b, 0, 0.02);
      out.push(
        piece({
          x: fx - 0.01,
          y: fy - 0.01,
          w: 0.02,
          d: 0.02,
          pieces: [],
          badge: {
            x: b.x + b.w / 2,
            y: b.y + b.h / 2,
            id: p.id,
            icon: `producers_${p.id}`,
            lift: units.reduce((t, u) => Math.max(t, unitTop(u)), 0.5) + 0.45,
          },
        }),
      );
    }
    return out;
  }
  if (p.t === 'meadow' && p.mark) {
    for (const u of markUnits(p.mark, b.x + b.w / 2, b.y + b.h / 2)) out.push(piece(u));
    return out;
  }
  if (p.t === 'meadow' && p.ground) {
    // D327: a monument ground: each later race day's mark, small, in rows: a trophy on a plinth.
    p.ground.marks.slice(0, 8).forEach((m, i) => {
      const x = b.x + 0.5 + (i % 4) * ((b.w - 1) / 3),
        y = b.y + 0.6 + Math.floor(i / 4) * (b.h - 1.2);
      out.push(
        piece({
          x: x - 0.2,
          y: y - 0.2,
          w: 0.4,
          d: 0.4,
          pieces: [
            {
              s: 'box',
              x: x - 0.15,
              y: y - 0.1,
              w: 0.3,
              d: 0.2,
              z: 0,
              h: 0.14,
              c: '#9EA19C',
              roof: true,
            },
            { s: 'cyl', x, y, r: 0.04, z: 0.14, h: 0.08, c: m === 'podium' ? M.gold : C.copper },
            { s: 'ball', x, y, r: 0.06, z: 0.2, c: m === 'podium' ? M.gold : C.copper },
          ],
        }),
      );
    });
    return out;
  }
  if (p.t === 'meadow') {
    // D422: the paddock fill on bare land two by two or larger, from a seed both maps share.
    const { rects, left } = fillRects(p.tiles);
    for (const q of rects) out.push(piece(fillUnit(q.x, q.y, q.w, q.h, fillSeed(q.x, q.y))));
    const r = rnd(b.x * 31 + b.y * 17 + 3);
    for (const [x, y] of scatter(left, r, Math.round(left.length * 0.12), 0.3)) {
      const s = 0.8 + r() * 0.4;
      out.push(
        piece({
          x: x - 0.15,
          y: y - 0.15,
          w: 0.3,
          d: 0.3,
          pieces: [
            { s: 'cyl', x, y, r: 0.035, z: 0, h: 0.22 * s, c: '#6B5A44' },
            { s: 'ball', x, y, r: 0.22 * s, z: 0.14 * s, c: '#3F7A44' },
          ],
        }),
      );
    }
    return out;
  }
  if (p.t === 'yard') {
    // The team's cars parked in the paddock's front bays.
    const y = b.y + b.h - 0.55;
    for (let x = b.x + 0.75, i = 0; x < b.x + b.w - 0.4; x += 1, i++) {
      if (hashXY(x, y, 21) < 0.35) continue;
      out.push(
        piece({
          x: x - 0.25,
          y: y - 0.3,
          w: 0.5,
          d: 0.6,
          pieces: car(
            x,
            y,
            false,
            i % 3 ? 'saloon' : 'gt',
            LIVERIES[(i * 4) % LIVERIES.length] as string,
          ),
        }),
      );
    }
  }
  return out;
}

/**
 * D422: a placeholder for the module's piece, its pieces painted without `paintUnit`'s soft shade
 * sized to its footprint (a whole plot or yard, far too wide); a hero takes the module's own
 * shade instead, a pool under its 3 by 3 middle.
 */
function paintAsset(u: Unit, P: Palette, o: string[]): void {
  const a = (u as AssetUnit).asset;
  if (a?.kind === 'hero' && !isPlan()) {
    const [cx, cy] = project(a.x + a.w / 2, a.y + a.h / 2);
    o.push(
      `<ellipse cx="${f1(cx)}" cy="${f1(cy)}" rx="${f1(2.7 * TW * 0.5)}" ry="${f1(2.7 * TH * 0.5)}" fill="rgba(30,32,36,.14)"/>`,
    );
  }
  const L = lookOf(P);
  for (const q of ordered(u)) paintPiece(q, P, L, o);
}

function paintMotorsportProp(s: Prop, P: Palette, o: string[]): void {
  if (s.k === 'piece') {
    const u = s.unit as Unit;
    return (u as AssetUnit).asset ? paintAsset(u, P, o) : paintUnit(u, P, o);
  }
  if (s.k === 'build') {
    // D423: the heart and its store's bays, each without a footprint-wide shade.
    for (const u of motorsportBuildingUnits(s))
      ((s as IsoBuildingAt).heart ? paintAsset : paintUnit)(u, P, o);
    return;
  }
  paintProp(s, P, o);
}

/** Every unit standing in the paddock: the buildings', the plots' and the marks'. */
export function allUnits(buildings: IsoBuildingAt[], parcels: IsoParcel[]): Unit[] {
  const L: Look = { night: false, winter: false };
  const out = buildings.flatMap((b) => motorsportBuildingUnits(b));
  for (const p of parcels)
    for (const s of motorsportProps(p, false, L)) if (s.k === 'piece') out.push(s.unit as Unit);
  return out;
}

/** How tall the paddock stands, in world units, for the 3D camera. */
export function paddockTop(buildings: IsoBuildingAt[], parcels: IsoParcel[]): number {
  let t = 1.2;
  for (const u of allUnits(buildings, parcels)) t = Math.max(t, unitTop(u));
  return t + 0.15;
}

/* ---------- the kit ---------- */

/** D420: the motorsport theme's map kit. */
export const motorsportKit: MapKit = {
  id: 'motorsport',
  plots: motorsportPlots(),
  chainBuildings: motorsportChains(),
  landscape: motorsportLandscape,
  edgeWater: false,
  fieldCycle: FIELD_CYCLE,
  palette: motorsportPalette,
  phaseTime: (phase) => PHASE_TIME[phase] ?? 'morning',
  light: (phase) => PHASE_LIGHT[phase] ?? PHASE_LIGHT.day!,
  goodWeather: GOOD_WEATHER,
  sky: (P, phase) => {
    const light = PHASE_LIGHT[phase] ?? PHASE_LIGHT.day!;
    return mix(P.far(P.pasture), light.haze, light.hazeT);
  },
  // D345: the shared badge's colours; the points icon when a row has none of its own.
  readyBadge: { ...READY_BADGE, fallback: 'currency_points' },
  // D422, D423: the heart on the yard, the store part of it; each plot's units counted.
  heart: true,
  dress: (p, { owned }) => {
    p.owned = owned;
  },
  top: paddockTop,
  tall: true,
  flat: {
    scenery: paintCountry,
    ground: paintGround,
    fieldJob: () => [],
    machine: () => {},
    edges: paintEdges,
    plotEdges: paintPlotEdges,
    props: (p, showSigns) => motorsportProps(p, showSigns),
    prop: paintMotorsportProp,
    standing: (buildings, parcels): Standing[] =>
      allUnits(buildings, parcels).map((u) => ({
        x: u.x,
        y: u.y,
        w: u.w,
        h: u.d,
        z: unitTop(u) * ZPX + 6,
      })),
    weather: paintWeather,
    // Exhaust off the dyno cells' stacks.
    smoke: (_buildings, parcels = []) => {
      const out: Puff[] = [];
      for (const p of parcels) {
        if (p.id !== 'dyno_rooms' || out.length >= 9) continue;
        const units = motorsportProps(p, false).flatMap((s) =>
          s.k === 'piece' ? [s.unit as Unit] : [],
        );
        // D426: from the module's dyno cells' own stacks.
        for (const [x, y, z] of dynoTops(units).slice(0, 3)) {
          const [cx, cy] = project(x, y, z * ZPX + 2);
          [0, 3.1, 6.2].forEach((delay, i) =>
            out.push({ cx: cx + i, cy, r: 3 + i * 1.5, delay: delay + (x % 3) }),
          );
        }
      }
      return out;
    },
    ripples: () => [],
  },
};
